import classnames from 'classnames/bind';
import React, { useEffect, useMemo, useRef } from 'react';
import styles from './MiniScreen.scss';
import { FocusTrapZone } from 'focus-trap-zone';
import { PortalWrapper } from 'portal-wrapper';
import { RouteScreenContext } from '../context/RouteScreenContext';
import { useApplySafeAreaPadding } from 'mini-hooks/lib/useApplySafeAreaPadding';
import { useFocusTransition } from '../hooks/useFocusTransition';
import { useParams } from 'mini-react-router-dom';
import type { MiniRouteScreenProps, RouteScreenData } from '../schema/MiniRouteScreen.types';

const cx = classnames.bind(styles);

/**
 * Component to create the Route Screen component with the header, body and footer
 */
const MiniRouteScreen: React.FC<MiniRouteScreenProps> = props => {
    const {
        onRouteEntry,
        onRouteExit,
        id,
        headerElement,
        bodyElement,
        footerElement,
        descendentRoutes,
        noSafeArea,
        className,
    } = props;

    const params = useParams();

    useEffect(() => {
        onRouteEntry?.(params);
        return () => onRouteExit?.(params);
        /* eslint-disable-next-line react-hooks/exhaustive-deps --
         * See aka.ms/mini-lint-errors
         *	> React Hook useEffect has missing dependencies: 'onRouteEntry' and 'onRouteExit'. Either include them or remove the dependency array. */
    }, [params]);

    const safeAreaRef = useRef<HTMLDivElement>(null);
    useApplySafeAreaPadding(safeAreaRef, noSafeArea);

    const { focusOnClose, focusOnOpen } = useFocusTransition(props);

    const routeScreenDataForDescendentScreens: RouteScreenData = useMemo(
        () => ({
            parentScreenRefToFocus: focusOnOpen,
        }),
        [focusOnOpen]
    );

    return (
        <RouteScreenContext.Provider value={routeScreenDataForDescendentScreens}>
            <FocusTrapZone
                id={id}
                hideBackground
                focusOnOpen={focusOnOpen}
                focusOnClose={focusOnClose}>
                <PortalWrapper divOrIdToAttachTo={id}>
                    <div className={cx('miniScreen', className)} ref={safeAreaRef}>
                        <div className={styles.header}>{headerElement}</div>
                        <div className={styles.body}>{bodyElement}</div>
                        <div className={styles.footer}>{footerElement}</div>
                    </div>
                </PortalWrapper>
            </FocusTrapZone>

            {/* Stack child routes on top of current screen*/}
            {descendentRoutes}
        </RouteScreenContext.Provider>
    );
};

export { MiniRouteScreen };
